import { Button } from "@/components/ui/button";
import { useState } from "react";
import * as Tooltip from "@radix-ui/react-tooltip";
import "./daily-workflow.css";
import { Info } from "lucide-react";

export interface MetricPresetsProps {
  selected: string[];
  onSelect: (keys: string[]) => void;
}

export interface MetricHelpProps {
  metric?: string;
  label: string;
  help?: string;
  inline?: boolean;
}

export interface CaptureStatusProps {
  watching: boolean;
  importing: boolean;
  snapshots: number;
  onScan: () => void;
  busy: boolean;
  message?: string;
}

type PresetId = "growth" | "complexity" | "duplication" | "checks" | "cleanup" | "dependencies";

const GROWTH_KEYS: string[] = ["sourceLoc", "testLoc"];
export const COMPLEXITY_COUNT_KEYS: string[] = ["ccAbove", "cognitiveAbove", "largeFunctions"];
export const COMPLEXITY_PCT_KEYS: string[] = ["ccAbovePct", "cognitiveAbovePct", "largeFunctionsPct"];
const DUPLICATION_KEYS: string[] = ["cloneGroups", "duplicationPct"];
const CHECKS_KEYS: string[] = ["compilerErrors", "oxlintErrors", "oxlintWarnings"];


function sameKeys(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  for (const k of b) {
    if (!set.has(k)) return false;
  }
  return true;
}

const PRESET_QUESTIONS: Record<PresetId, string> = {
  growth: "Is test code keeping pace with source growth?",
  complexity: "How much code exceeds complexity limits?",
  duplication: "Is duplication spreading or contained?",
  checks: "Are compiler and lint checks clean?",
  cleanup: "Where is unused code accumulating?",
  dependencies: "Are dependency cycles increasing?",

};

export function chartPreset(selected: string[]): PresetId | null {
  if (sameKeys(selected, GROWTH_KEYS)) return "growth";
  if (sameKeys(selected, COMPLEXITY_COUNT_KEYS) || sameKeys(selected, COMPLEXITY_PCT_KEYS)) return "complexity";
  if (sameKeys(selected, DUPLICATION_KEYS)) return "duplication";
  if (sameKeys(selected, CHECKS_KEYS)) return "checks";
  if (sameKeys(selected, ["knipUnusedFiles","knipUnusedExports","knipUnusedDeps"])) return "cleanup";
  if (sameKeys(selected, ["depcruiseCycles","fallowCycles"])) return "dependencies";
  return null;
}

export function chartQuestion(selected: string[]) {
  const active=chartPreset(selected);
  return active ? PRESET_QUESTIONS[active] : "How are these measurements changing?";
}

export function MetricPresets({ selected, onSelect }: MetricPresetsProps) {
  const active=chartPreset(selected);
  const presets: {id:PresetId;label:string;keys:string[]}[] = [
    {id:"growth",label:"Growth",keys:GROWTH_KEYS},
    {id:"complexity",label:"Complexity",keys:selected.includes("ccAbovePct")?COMPLEXITY_PCT_KEYS:COMPLEXITY_COUNT_KEYS},
    {id:"duplication",label:"Duplication",keys:DUPLICATION_KEYS},
    {id:"checks",label:"Checks",keys:CHECKS_KEYS},
    {id:"cleanup",label:"Unused code",keys:["knipUnusedFiles","knipUnusedExports","knipUnusedDeps"]},
    {id:"dependencies",label:"Dependencies",keys:["depcruiseCycles","fallowCycles"]},
  ];
  return <div className="dw-presets"><div className="dw-toolbar" role="group" aria-label="Metric presets">
    {presets.map(preset=><Button key={preset.id} variant="ghost" type="button"
      className="dw-preset rounded-none border-0 border-b-2 border-transparent bg-transparent dark:bg-transparent aria-pressed:border-primary aria-pressed:text-primary aria-pressed:bg-transparent dark:aria-pressed:bg-transparent"
      aria-pressed={active===preset.id} onClick={()=>onSelect([...preset.keys])}>{preset.label}</Button>)}
  </div></div>;
}

const HELP_COPY: Record<string, { body: string; source: string }> = {
  sourceLoc: {
    body: "Source lines, test files excluded. Baseline for growth.",
    source: "Measured by cloc.",
  },
  testLoc: {
    body: "Test lines counted separately. Compare with source to see if tests keep pace.",
    source: "Measured by cloc.",
  },
  ccAbove: {
    body: "Cyclomatic complexity counts independent paths through decision logic. These functions exceed the threshold recorded for this scan. Flags review candidates, not grades.",
    source: "Measured by Fallow complexity.",
  },
  cognitiveAbove: {
    body: "Cognitive complexity estimates difficulty following control flow, including nesting. These functions exceed the threshold recorded for this scan. Flags review candidates, not grades.",
    source: "Measured by Fallow complexity.",
  },
  largeFunctions: {
    body: "Functions whose source span in lines exceeds the threshold set for this scan. Flags review candidates, not grades.",
    source: "Measured by Fallow complexity.",
  },
  ccAbovePct: {
    body: "% of measured functions above the cyclomatic threshold. Denominator includes only functions with this measurement.",
    source: "Measured by Fallow complexity.",
  },
  cognitiveAbovePct: {
    body: "% of measured functions above the cognitive threshold. Denominator includes only functions with this measurement.",
    source: "Measured by Fallow complexity.",
  },
  largeFunctionsPct: {
    body: "% of measured functions above the size threshold. Denominator includes only functions with this measurement.",
    source: "Measured by Fallow complexity.",
  },
  cloneGroups: {
    body: "Duplicate code groups. One group can span many lines.",
    source: "Measured by Fallow duplication.",
  },
  duplicationPct: {
    body: "Share of code inside clones. Different units from group count · read each line on its own scale.",
    source: "Measured by Fallow duplication.",
  },
  compilerErrors: {
    body: "TypeScript compiler errors in this snapshot.",
    source: "Reported by the TypeScript compiler.",
  },
  oxlintErrors: {
    body: "Error-level findings in this snapshot.",
    source: "Reported by Oxlint.",
  },
  oxlintWarnings: {
    body: "Warning-level findings in this snapshot.",
    source: "Reported by Oxlint.",
  },
  ccPerKloc: {
    body: "Density: total cyclomatic complexity ÷ source LOC × 1000. A rate per thousand lines, not a percent.",
    source: "Derived from Fallow complexity and cloc LOC.",
  },
  cognitivePerKloc: {
    body: "Density: total cognitive complexity ÷ source LOC × 1000. A rate per thousand lines, not a percent.",
    source: "Derived from Fallow complexity and cloc LOC.",
  },
  p95Cyclomatic: {
    body: "95th-percentile cyclomatic score: at least 95% of measured functions score at or below it. Shows the high end, not the average.",
    source: "Derived from Fallow complexity.",
  },
};

export function MetricHelp({ label, metric=label, help, inline=false }: MetricHelpProps) {
  const [open, setOpen] = useState(false);
  const entry = HELP_COPY[metric];
  const body = help ?? (entry
    ? entry.body
    : `Recorded measurement "${metric}". Interpret alongside the chart scale.`);
  const source = help ? "" : entry ? entry.source : "Recorded by the local scan.";

  return (
    <Tooltip.Provider delayDuration={150}>
      <Tooltip.Root open={open} onOpenChange={setOpen}>
        <Tooltip.Trigger asChild>
          <Button variant="ghost" type="button" className="dw-help-btn" ref={el=>{if(el)for(const [key,value] of Object.entries({position:inline?"static":"absolute",top:inline?"auto":"6px",right:inline?"auto":"12px",width:"24px",height:"24px","min-width":"24px","min-height":"24px",padding:"0","border-radius":"6px",background:"transparent",color:"var(--muted)",border:"0","box-shadow":"none"}))el.style.setProperty(key,value,"important");}} aria-label={`About ${label}`}
            aria-expanded={open} onClick={(event) => { event.preventDefault(); setOpen(!open); }}>
            <Info size={14} strokeWidth={1.6} aria-hidden="true" />
          </Button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content className="dw-tooltip" sideOffset={8} collisionPadding={12}>
            <span className="dw-tooltip-body">{body}</span>{" "}
            <span className="dw-tooltip-src">{source}</span>{" "}

          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}

export function CaptureStatus({
  watching,
  importing,
  snapshots,
  onScan,
  busy,
  message,
}: CaptureStatusProps) {
  return (
    <section className="dw-capture" aria-labelledby="dw-collection-heading">
      <h2 id="dw-collection-heading" className="dw-h">
        Code snapshots
      </h2>
      <dl className="dw-status">
        <div className="dw-row">
          <dt>Commit scans</dt>
          <dd data-state={watching ? "on" : "off"}><span aria-hidden="true"/>{watching ? "On" : "Off"}</dd>
        </div>
        <div className="dw-row">
          <dt>Agent activity</dt>
          <dd data-state={importing ? "on" : "off"}><span aria-hidden="true"/>{importing ? "On" : "Off"}</dd>
        </div>
      </dl>
      <p className="dw-note">
        Runs only while the app is open. {snapshots}{" "}
        {snapshots === 1 ? "snapshot" : "snapshots"} saved.
      </p>
      <div className="dw-scanrow">
        <Button variant="default"
          type="button"
          className="dw-scanbtn"
          onClick={onScan}
          disabled={busy}
        >
          {busy ? "Scanning…" : "Measure latest commit"}
        </Button>
        <a className="dw-link" href="#compare">
          Measure older commits
        </a>
      </div>
      <p className="dw-note">Measures code size, complexity and duplication in your latest commit. Excludes uncommitted edits. Local CPU only; no AI tokens.</p>
      {message ? (
        <p className="dw-message" role="status">
          {message}
        </p>
      ) : null}
    </section>
  );
}

export function AgentWorkIntro() {
  return (
    <section className="dw-agent" aria-labelledby="dw-agent-heading">
      <h2 id="dw-agent-heading" className="dw-h">
        Session summaries
      </h2>
      <p className="dw-note">
        Short notes on outcomes, obstacles, and what was learned.
      </p>
      <p className="dw-note">
        Recorded by your agent at completion. No manual session approval is required.
      </p>
      <details className="dw-details">
        <summary>Missing a summary?</summary>
        <p className="dw-note">
          Summaries are written only when a session runs through the flow. Start
          with <code>phasedrift session start</code> on entry and use <code>phasedrift session review --help</code> for the review command at completion. Installing the skill does not recreate missing events.
        </p>
      </details>
    </section>
  );
}

export function InvestigationIntro() {
  return (
    <section className="dw-invest" aria-labelledby="dw-invest-heading">
      <h2 id="dw-invest-heading" className="dw-h">
        Explore your code
      </h2>
      <p className="dw-note">
        Browse folders, select a file, then inspect its complexity and findings.
      </p>
      <p className="dw-links">
        <a className="dw-link" href="#compare">
          Compare
        </a>{" "}
        <span aria-hidden="true">·</span>{" "}
        <a className="dw-link" href="#changes">
          Changes
        </a>
      </p>
    </section>
  );
}
